import { useQueries, useQuery } from '@tanstack/react-query';
import {
  configControllerGetPublicProductConfigQueryOptions,
  customerControllerGetCustomerQueryOptions,
  type PolicyDto,
} from 'korint-sdk';
import { useMemo } from 'react';
import { useAccessibleCustomerIds } from './useAccessibleCustomerIds';

export function useAccessiblePayerCustomerIds(
  policy: Pick<PolicyDto, 'billingAssignment' | 'customers' | 'productId'>,
): string[] {
  const accessibleCustomerIds = useAccessibleCustomerIds(policy.customers);
  const hasBillingAssignment = !!policy.billingAssignment;

  // Only fetch config + customer data when billingAssignment is NOT on the policy
  // (needs role resolution from config default)
  const { data: publicProductConfig } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
    enabled: !hasBillingAssignment,
  });

  const customerQueries = useQueries({
    queries: hasBillingAssignment
      ? []
      : accessibleCustomerIds.map(id => ({
          ...customerControllerGetCustomerQueryOptions(id),
        })),
  });

  return useMemo(() => {
    // Case 1: resolved billing assignment on policy
    if (policy.billingAssignment) {
      const payerIds = new Set([
        policy.billingAssignment.defaultPayerId,
        ...(policy.billingAssignment.componentOverrides ?? []).map(o => o.payerCustomerId),
      ]);
      return accessibleCustomerIds.filter(id => payerIds.has(id));
    }

    // Case 2: resolve from config default + customer roles
    const billingAssignmentConfig = publicProductConfig?.billing?.billingAssignment;
    if (!billingAssignmentConfig || customerQueries.some(q => q.isLoading)) {
      return accessibleCustomerIds;
    }

    const defaultConfig = billingAssignmentConfig.defaultConfig;
    const payerRoles = new Set([
      defaultConfig.defaultPayerRole,
      ...(defaultConfig.componentOverrides ?? []).map(o => o.payerRole),
    ]);

    return accessibleCustomerIds.filter((_id, index) => {
      /**
       * @FIXME: Are we sure customerQueries and accessibleCustomerIds are in the same order ?
       */
      const customer = customerQueries[index]?.data;
      return customer ? payerRoles.has(customer.role) : false;
    });
  }, [policy.billingAssignment, accessibleCustomerIds, publicProductConfig, customerQueries]);
}
